/**
 * Read-only mirror of the ODE-P04-T09 journey-history derivation contract
 * (REQ-040/041/042/046) — ODE-P07-T06.
 *
 * The server is authoritative for every derivation: the journey-history read
 * endpoints group a journey per tracking session (REQ-040), derive stop
 * points with a dwell-time threshold + cluster radius (REQ-041) and derive
 * the cumulative haversine distance (REQ-042) server-side over the ingested
 * tracking samples. This module carries the DOCUMENTED server-side bounds and
 * defaults only, so the admin page can show the operator which derivation
 * configuration produced the numbers it renders.
 *
 * Nothing here is applied to the data: the client never recomputes,
 * extrapolates or re-derives a journey value (approved DND — server
 * authoritative for derivation). The panel that renders these values is
 * labelled as informational and non-authoritative.
 *
 * Values mirror `apps/ode-api/src/journey-history/types.ts`
 * (`JOURNEY_HISTORY_*` constants) at the verified ODE-P04-T09 candidate
 * (3eff6d2). They are a client mirror for display only — exactly like
 * `rbac/catalog.ts` mirrors the backend permission catalog.
 *
 * Traces to: ODE-PRD-REQ-040, ODE-PRD-REQ-041, ODE-PRD-REQ-042,
 *            ODE-PRD-REQ-046
 */

/** Version of the journey-history read contract consumed by this module. */
export const JOURNEY_HISTORY_SCHEMA_VERSION = 1 as const;

/** Default page size of the server-side journey list (REQ-046). */
export const JOURNEY_LIST_DEFAULT_PAGE_SIZE = 20 as const;

/** Bounded page size: 1..100 (mirror of the server bound). */
export const JOURNEY_LIST_MAX_PAGE_SIZE = 100 as const;

/** Bounded polyline response: the server caps the route at this many points. */
export const JOURNEY_MAX_POLYLINE_POINTS = 10_000 as const;

/** Bounded date-range filter: at most 366 days per query. */
export const JOURNEY_MAX_DATE_RANGE_DAYS = 366 as const;

/** Default stop-point dwell threshold (REQ-041): 120 000 ms (2 minutes). */
export const JOURNEY_STOP_DWELL_MS_DEFAULT = 120_000 as const;

/** Bounds for the stop-point dwell threshold (REQ-041). */
export const JOURNEY_STOP_DWELL_MS_MIN = 10_000 as const;
export const JOURNEY_STOP_DWELL_MS_MAX = 3_600_000 as const;

/** Default stop-cluster radius (REQ-041): 100 m. */
export const JOURNEY_CLUSTER_RADIUS_METERS_DEFAULT = 100 as const;

/** Bounds for the stop-cluster radius (REQ-041). */
export const JOURNEY_CLUSTER_RADIUS_METERS_MIN = 10 as const;
export const JOURNEY_CLUSTER_RADIUS_METERS_MAX = 1_000 as const;

/** Default Earth radius for the haversine distance derivation (REQ-042). */
export const JOURNEY_EARTH_RADIUS_METERS_DEFAULT = 6_371_008.8 as const;

/** Default minimum segment distance: 0 m (REQ-042). */
export const JOURNEY_MIN_SEGMENT_METERS_DEFAULT = 0 as const;

/** Upper bound for the minimum segment distance filter (REQ-042). */
export const JOURNEY_MIN_SEGMENT_METERS_MAX = 1_000 as const;

/** One read-only derivation-config row rendered by the admin page. */
export interface JourneyDerivationConfigEntry {
  /** Stable key (deterministic `data-testid` suffix + React key). */
  readonly key: string;
  /** Plain-Indonesian label of the derivation parameter. */
  readonly label: string;
  /** Documented server default for this parameter. */
  readonly defaultValue: string;
  /** Documented server-side bounds for this parameter. */
  readonly bounds: string;
  /** Requirement the parameter belongs to. */
  readonly requirement: string;
}

/**
 * The documented server-side derivation configuration (REQ-041/042) plus the
 * response bounds the page renders against. Display only — the running
 * server may be configured differently; the values below are the documented
 * contract defaults, never a client-applied computation.
 */
export const JOURNEY_DERIVATION_CONFIG: readonly JourneyDerivationConfigEntry[] = [
  {
    key: 'stop-dwell',
    label: 'Ambang dwell titik henti',
    defaultValue: `${JOURNEY_STOP_DWELL_MS_DEFAULT} ms (2 menit)`,
    bounds: `${JOURNEY_STOP_DWELL_MS_MIN}..${JOURNEY_STOP_DWELL_MS_MAX} ms`,
    requirement: 'REQ-041',
  },
  {
    key: 'cluster-radius',
    label: 'Radius klaster titik henti',
    defaultValue: `${JOURNEY_CLUSTER_RADIUS_METERS_DEFAULT} m`,
    bounds: `${JOURNEY_CLUSTER_RADIUS_METERS_MIN}..${JOURNEY_CLUSTER_RADIUS_METERS_MAX} m`,
    requirement: 'REQ-041',
  },
  {
    key: 'earth-radius',
    label: 'Radius bumi (haversine)',
    defaultValue: `${JOURNEY_EARTH_RADIUS_METERS_DEFAULT} m`,
    bounds: '6 300 000..6 500 000 m',
    requirement: 'REQ-042',
  },
  {
    key: 'min-segment',
    label: 'Jarak segmen minimum',
    defaultValue: `${JOURNEY_MIN_SEGMENT_METERS_DEFAULT} m`,
    bounds: `0..${JOURNEY_MIN_SEGMENT_METERS_MAX} m`,
    requirement: 'REQ-042',
  },
  {
    key: 'polyline-bound',
    label: 'Batas titik rute per detail',
    defaultValue: `${JOURNEY_MAX_POLYLINE_POINTS} titik`,
    bounds: 'tetap (server)',
    requirement: 'REQ-040',
  },
  {
    key: 'page-size',
    label: 'Batas baris daftar perjalanan',
    defaultValue: `${JOURNEY_LIST_DEFAULT_PAGE_SIZE} baris`,
    bounds: `1..${JOURNEY_LIST_MAX_PAGE_SIZE} baris`,
    requirement: 'REQ-046',
  },
  {
    key: 'date-range',
    label: 'Rentang tanggal filter',
    defaultValue: 'tak terbatas di dalam batas',
    bounds: `maksimum ${JOURNEY_MAX_DATE_RANGE_DAYS} hari`,
    requirement: 'REQ-046',
  },
];
